import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AuthContext } from '@/context/auth-context'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { RootState } from '@/store/store'
import { GlobalToastType } from '@/constants/constants-ui'
import { kRouteSegments } from '@/routing/paths'
import { kNewConversationTitle } from '@/chat-common/conversation_list/constants'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useCreateNewAgentChat } from '@/agent/conversations/hooks/useNewAgentChat'
import { NewChat } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { NewChatSelectMatterModal } from './NewChatSelectMatterModal'
import { selectActiveMatter } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'

type NewChatMatterBoundButtonProps = {
  /**
   * If provided, called when the user clicks a CTA
   */
  onClick?: () => void
  /**
   * Whether the button is compact i.e displays as an icon only
   */
  iconOnly?: boolean
}

export default function NewChatMatterBoundButton({ iconOnly = false, onClick }: NewChatMatterBoundButtonProps) {
  const dispatch = useAppDispatch()

  // Auth Context
  const authContext = useContext(AuthContext)
  const userIsAnon = authContext?.user?.isAnonymous ?? false

  const { chatFeature } = useParams()
  const { trackEvent } = useAnalytics()
  const { createNewAgentChat } = useCreateNewAgentChat()

  // Local state
  const [clickDisabled, setClickDisabled] = useState<boolean>(false)
  const [showSelectMatterModal, setShowSelectMatterModal] = useState<boolean>(false)

  // Active Matter ID
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const activeMatter = useAppSelector(selectActiveMatter)
  const isActiveMatterUnassigned = isUnassignedMatter(activeMatter)
  const isStandardMatterActive = !!activeMatterId && !isActiveMatterUnassigned

  const handleCreateNewChat = async (matterId: string | null) => {
    // If the button is disabled, do not allow another click
    if (clickDisabled) return

    // Disable for multiple clicks
    setClickDisabled(true)

    // Validate the chat feature
    if (chatFeature !== kRouteSegments.assistant) {
      console.error('Invalid chat feature:', chatFeature)

      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Error creating chat. Please try again.',
          durationMs: 2000,
        })
      )

      setClickDisabled(false)
      return
    }

    function onSuccess() {
      setClickDisabled(false)

      // Track the analytics event on a successful click
      trackEvent(AnalyticsEvent.NewChat, {
        ...(matterId && { conversationMatterId: matterId }),
        skill: '',
        skillSource: '',
      })

      onClick?.()
    }

    function onError(e: unknown) {
      console.error('Error creating a new chat:', e)

      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Error creating chat. Please try again.',
          durationMs: 2000,
        })
      )

      setClickDisabled(false)
    }

    createNewAgentChat(true, matterId, onSuccess, onError)
  }

  const handleButtonClick = () => {
    // If the user is anonymous (no matter) or there is an active standard matter, create a new chat
    if (userIsAnon || isStandardMatterActive) {
      handleCreateNewChat(activeMatterId)
    } else {
      setShowSelectMatterModal(true)
    }
  }

  const handleSelectMatterModalDismiss = () => {
    setShowSelectMatterModal(false)
    onClick?.()
  }

  const handleSelectMatterModalContinue = (matterId: string) => {
    setShowSelectMatterModal(false)
    setClickDisabled(true)
    // Create the chat with the selected matter ID
    handleCreateNewChat(matterId)
  }

  return (
    <>
      {iconOnly ? (
        <IconButton
          aria-label="Start new chat"
          color="primary"
          data-testid="new-chat-button-icon-only"
          disabled={clickDisabled}
          icon={<NewChat />}
          onClick={handleButtonClick}
          variant="plain"
          size="lg"
        />
      ) : (
        <Button
          aria-label="Start new chat"
          color="primary"
          data-testid="new-chat-button"
          disabled={clickDisabled}
          fullWidth
          onClick={handleButtonClick}
          title="New Chat"
          variant={isStandardMatterActive ? 'filled' : 'outlined'}
        >
          {isStandardMatterActive || userIsAnon ? kNewConversationTitle : 'Start Chat in Matter'}
        </Button>
      )}
      <NewChatSelectMatterModal
        open={showSelectMatterModal}
        onOpenChange={setShowSelectMatterModal}
        onDismiss={handleSelectMatterModalDismiss}
        onContinue={handleSelectMatterModalContinue}
        showExistingChatsHint
      />
    </>
  )
}
